// Palco da Apresentação de Diagnóstico: escala o slide e edita o texto no lugar.
//
// **Escala.** O slide tem largura FIXA de 338mm — ele é literalmente a página
// que o WeasyPrint produz. Escalá-lo por transform (em vez de reflowá-lo em
// unidades relativas) é o que garante que o preview seja o arquivo: mesma
// quebra de linha, mesma altura de tabela, mesmo corte de gráfico. Precisa de
// JS porque `calc()` não divide comprimento por comprimento. O fator considera
// LARGURA E ALTURA — o slide corrente tem de caber inteiro, sem scroll.
//
// **Edição.** Duplo clique num nó `[data-deck-edit]` o torna editável no
// próprio slide; Enter (ou sair do campo) salva, Escape desfaz. O servidor
// recebe só `{field, value}` e revalida — o HTML editado nunca volta ao
// servidor.
const MM_TO_PX = 96 / 25.4;
const SLIDE_W = 338 * MM_TO_PX;
const SLIDE_H = 190 * MM_TO_PX;

export default {
  mounted() {
    this.fit();
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(this.el);
    }
    this.bindEditing();
  },

  updated() {
    this.fit();
  },

  destroyed() {
    if (this.observer) this.observer.disconnect();
  },

  fit() {
    const width = this.el.clientWidth;
    const height = this.el.clientHeight;
    if (!width) return;

    // Sem altura definida (primeiro frame, container ainda medindo), cai na
    // largura: melhor um slide grande demais por um frame que nenhum slide.
    const scale = height
      ? Math.min(width / SLIDE_W, height / SLIDE_H)
      : width / SLIDE_W;

    this.el.style.setProperty("--deck-scale", String(scale));
    // Centraliza a sobra no eixo em que o slide não encostou.
    this.el.style.setProperty(
      "--deck-offset-x",
      `${Math.max((width - SLIDE_W * scale) / 2, 0)}px`,
    );
    this.el.style.setProperty(
      "--deck-offset-y",
      `${Math.max(((height || SLIDE_H * scale) - SLIDE_H * scale) / 2, 0)}px`,
    );
  },

  bindEditing() {
    this.el.addEventListener("dblclick", (event) => {
      const node = event.target.closest("[data-deck-edit]");
      if (!node || !this.el.contains(node)) return;

      event.preventDefault();
      this.startEditing(node);
    });
  },

  startEditing(node) {
    if (node.getAttribute("contenteditable") === "true") return;

    const original = node.innerText;
    node.setAttribute("contenteditable", "true");
    node.spellcheck = false;
    node.focus();
    this.selectAll(node);

    const finish = (save) => {
      node.removeAttribute("contenteditable");
      node.removeEventListener("keydown", onKey);
      node.removeEventListener("blur", onBlur);

      const value = node.innerText.trim();

      if (!save || value === original.trim()) {
        node.innerText = original;
        return;
      }

      // O servidor recompila e devolve o slide; o texto na tela é só otimismo
      // até lá.
      this.pushEvent("deck_edit_text", {
        field: node.dataset.deckEdit,
        value: value,
      });
    };

    const onKey = (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        finish(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        finish(false);
      }
    };

    const onBlur = () => finish(true);

    node.addEventListener("keydown", onKey);
    node.addEventListener("blur", onBlur);
  },

  selectAll(node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  },
};
